/**
 * Unassign Fees Table Component
 * Displays fee unassign request history grouped by class
 */
import React from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import LoadingComponent from '../../loadingComponent';
import moment from 'moment';

/**
 * Unassign Fees Table Component - displays fee unassign request history
 * @param {Object} studentData - Student data
 * @param {Object} feeHistoryData - Fee history data from API (contains unassignHistory array)
 * @param {Boolean} isLoading - Loading state
 * @returns {JSX.Element} - Unassign Fees table component
 */
export const UnassignFeesTable = ({ studentData, feeHistoryData = null, isLoading = false }) => {
    const unassignFeesData = feeHistoryData?.unassignHistory || [];

    // Date formatting function using moment - converts to DD/MM/YYYY
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = moment(dateString);
        return date.isValid() ? date.format('DD/MM/YYYY') : dateString;
    };

    // Amount formatting template
    const amountBodyTemplate = (rowData) => {
        const amount = rowData.feeAmount;
        if (amount == null) return '0.00';
        return (
            <span style={{
                fontFamily: 'InterMedium',
                color: '#495057',
                fontSize: '0.9375rem'
            }}>
                {amount.toFixed(2)}
            </span>
        );
    };

    // Date formatting templates
    const requestDateBodyTemplate = (rowData) => {
        return formatDate(rowData.requestSentDate);
    };

    const approvedDateBodyTemplate = (rowData) => {
        return formatDate(rowData.approvedDate);
    };

    const groupedData = {};
    unassignFeesData.forEach(item => {
        const className = item.className || 'Unknown Class';
        if (!groupedData[className]) {
            groupedData[className] = [];
        }
        groupedData[className].push(item);
    });

    const statusBodyTemplate = (rowData) => {
        const statusStyles = {
            'Approved': {
                background: '#C8F4E3',
                color: '#065F46',
                padding: '6px 16px',
                borderRadius: '8px',
                fontSize: '0.875rem',
                fontWeight: '600',
                fontFamily: 'InterSemiBold'
            },
            'Pending': {
                background: '#FEF3C7',
                color: '#92400E',
                padding: '6px 16px',
                borderRadius: '8px',
                fontSize: '0.875rem',
                fontWeight: '600',
                fontFamily: 'InterSemiBold'
            },
            'Rejected': {
                background: '#FEE2E2',
                color: '#991B1B',
                padding: '6px 16px',
                borderRadius: '8px',
                fontSize: '0.875rem',
                fontWeight: '600',
                fontFamily: 'InterSemiBold'
            }
        };

        const style = statusStyles[rowData.status] || {
            background: '#E5E7EB',
            color: '#000000',
            border: 'none',
            borderRadius: '8px',
            padding: '6px 16px',
            fontSize: '0.875rem',
            fontWeight: '500'
        };

        return <span style={style}>{rowData.status}</span>;
    };

    if (isLoading) {
        return <LoadingComponent />;
    }

    return (
        <div className="tab-content feetype-wise-content">
            <div style={{
                fontFamily: 'InterSemiBold',
                fontSize: '1.125rem',
                color: '#1F2937',
                marginBottom: '1.5rem'
            }}>
                Fee Unassign Request History :
            </div>

            {Object.keys(groupedData).length > 0 ? (
                Object.entries(groupedData).map(([className, records]) => (
                    <div key={className} style={{ marginBottom: '2rem' }}>
                      {/*   {className !== 'Unknown Class' && (
                            <div style={{
                                fontFamily: 'InterSemiBold',
                                fontSize: '1rem',
                                color: '#1F2937',
                                marginBottom: '0.75rem'
                            }}>
                                {className}
                            </div>
                        )} */}
                        <div className="fee-table-container card datatable-crud-demo" style={{ overflow: 'auto' }}>
                            <DataTable
                                value={records}
                                className="fee-details-datatable"
                                showGridlines
                                stripedRows
                                responsiveLayout="scroll"
                            >
                                <Column field="feeType" header="FEE TYPE" style={{ minWidth: '150px' }} />
                                <Column field="feeAmount" header="FEE AMOUNT" body={amountBodyTemplate} style={{ minWidth: '150px' }} />
                                <Column field="status" header="STATUS" body={statusBodyTemplate} style={{ minWidth: '120px', textAlign: 'center' }} />
                                <Column field="reason" header="REASON" style={{ minWidth: '200px' }} />
                                <Column field="requestSentBy" header="REQUEST BY" style={{ minWidth: '150px' }} />
                                <Column field="requestSentDate" header="REQUEST DATE" body={requestDateBodyTemplate} style={{ minWidth: '150px' }} />
                                <Column field="approvedBy" header="APPROVED BY" style={{ minWidth: '150px' }} />
                                <Column field="approvedDate" header="APPROVED DATE" body={approvedDateBodyTemplate} style={{ minWidth: '150px' }} />
                                <Column field="approvedRemarks" header="APPROVED REMARKS" style={{ minWidth: '180px' }} />
                            </DataTable>
                        </div>
                    </div>
                ))
            ) : (
                <div className="fee-table-container card datatable-crud-demo" style={{ overflow: 'auto' }}>
                    <DataTable
                        value={[]}
                        className="fee-details-datatable"
                        showGridlines
                        stripedRows
                        responsiveLayout="scroll"
                        emptyMessage="No unassign fee history available"
                    >
                        <Column field="feeType" header="FEE TYPE" style={{ minWidth: '150px' }} />
                        <Column field="feeAmount" header="FEE AMOUNT" body={amountBodyTemplate} style={{ minWidth: '150px' }} />
                        <Column field="status" header="STATUS" body={statusBodyTemplate} style={{ minWidth: '120px', textAlign: 'center' }} />
                        <Column field="reason" header="REASON" style={{ minWidth: '200px' }} />
                        <Column field="requestSentBy" header="REQUEST BY" style={{ minWidth: '150px' }} />
                        <Column field="requestSentDate" header="REQUEST DATE" body={requestDateBodyTemplate} style={{ minWidth: '150px' }} />
                        <Column field="approvedBy" header="APPROVED BY" style={{ minWidth: '150px' }} />
                        <Column field="approvedDate" header="APPROVED DATE" body={approvedDateBodyTemplate} style={{ minWidth: '150px' }} />
                        <Column field="approvedRemarks" header="APPROVED REMARKS" style={{ minWidth: '180px' }} />
                    </DataTable>
                </div>
            )}
        </div>
    );
};
